import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";

interface EmailTemplate {
  id: string;
  template_key: string;
  company_name: string;
  logo_url: string | null;
  primary_color: string;
  greeting_text: string;
  footer_text: string;
  additional_info: string;
  contact_email: string;
}

const TRANSFER_DEMO = {
  booking_code: "TXB-A1B2C3",
  customer_name: "John Doe",
  customer_email: "john@example.com",
  customer_phone: "+1 555 0123",
  pickup_location: "Punta Cana Intl. Airport (PUJ)",
  dropoff_location: "Hotel Example Resort",
  service: "STANDARD",
  route_type: "Round-Trip",
  passengers: 2,
  pickup_date: "23/4/2026 1:30 PM",
  arrival_time: "1:30 PM",
  airline: "JetBlue",
  flight_number: "B6-1432",
  return_date: "28/4/2026 1:30 PM",
  return_airline: "JetBlue",
  return_flight: "B6-1433",
  total: "US$78.00",
  payment_method: "Cash",
};

const TOUR_DEMO = {
  booking_code: "TRB-X9Y8Z7",
  customer_name: "Jane Smith",
  customer_email: "jane@example.com",
  customer_phone: "+1 555 0456",
  tour_name: "Saona Island Full Day",
  destination: "Punta Cana",
  excursion_date: "25/4/2026",
  schedule: "8:00 AM - 5:00 PM",
  duration: "Full Day",
  adults: 2,
  children: 1,
  pickup_point: "Hard Rock Hotel",
  total: "US$195.00",
  payment_method: "PayPal",
};

export function EmailTemplatesSection() {
  const [templates, setTemplates] = useState<Record<string, EmailTemplate>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("booking_confirmation");

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    const { data, error } = await supabase
      .from("email_templates" as any)
      .select("*")
      .in("template_key", ["booking_confirmation", "tour_booking_confirmation"]);
    if (error) {
      toast.error("Error loading templates");
    } else if (data) {
      const map: Record<string, EmailTemplate> = {};
      (data as any[]).forEach((t) => { map[t.template_key] = t; });
      setTemplates(map);
    }
    setLoading(false);
  };

  const handleSave = async (key: string) => {
    const template = templates[key];
    if (!template) return;
    setSaving(true);
    const { error } = await supabase
      .from("email_templates" as any)
      .update({
        company_name: template.company_name,
        logo_url: template.logo_url,
        primary_color: template.primary_color,
        greeting_text: template.greeting_text,
        footer_text: template.footer_text,
        additional_info: template.additional_info,
        contact_email: template.contact_email,
      } as any)
      .eq("id", template.id);
    setSaving(false);
    if (error) toast.error("Error saving template");
    else toast.success("Template saved!");
  };

  const update = (key: string, field: keyof EmailTemplate, value: string) => {
    const t = templates[key];
    if (!t) return;
    setTemplates({ ...templates, [key]: { ...t, [field]: value } });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (Object.keys(templates).length === 0) {
    return <p className="text-muted-foreground">No templates found.</p>;
  }

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <TabsList className="mb-4">
        <TabsTrigger value="booking_confirmation">Transfers</TabsTrigger>
        <TabsTrigger value="tour_booking_confirmation">Tours</TabsTrigger>
      </TabsList>

      {["booking_confirmation", "tour_booking_confirmation"].map((key) => {
        const template = templates[key];
        if (!template) return (
          <TabsContent key={key} value={key}>
            <p className="text-muted-foreground">No template found for this type.</p>
          </TabsContent>
        );

        return (
          <TabsContent key={key} value={key}>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <TemplateForm template={template} saving={saving} onUpdate={(f, v) => update(key, f, v)} onSave={() => handleSave(key)} />
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Live Preview</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-[80vh] overflow-auto">
                    {key === "tour_booking_confirmation" ? (
                      <TourTemplatePreview template={template} />
                    ) : (
                      <TransferTemplatePreview template={template} />
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        );
      })}
    </Tabs>
  );
}

function TemplateForm({ template, saving, onUpdate, onSave }: {
  template: EmailTemplate;
  saving: boolean;
  onUpdate: (field: keyof EmailTemplate, value: string) => void;
  onSave: () => void;
}) {
  const pc = template.primary_color || "#2563eb";
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Edit Template</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label>Company Name</Label>
          <Input value={template.company_name} onChange={(e) => onUpdate("company_name", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Logo URL</Label>
          <Input value={template.logo_url || ""} onChange={(e) => onUpdate("logo_url", e.target.value)} placeholder="https://..." />
        </div>
        <div className="space-y-1.5">
          <Label>Primary Color</Label>
          <div className="flex gap-2 items-center">
            <input type="color" value={pc} onChange={(e) => onUpdate("primary_color", e.target.value)} className="h-10 w-10 rounded border cursor-pointer" />
            <Input value={pc} onChange={(e) => onUpdate("primary_color", e.target.value)} className="w-32" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Greeting Text</Label>
          <Input value={template.greeting_text} onChange={(e) => onUpdate("greeting_text", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Contact Email</Label>
          <Input value={template.contact_email} onChange={(e) => onUpdate("contact_email", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Footer Text</Label>
          <Input value={template.footer_text} onChange={(e) => onUpdate("footer_text", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Additional Information</Label>
          <Textarea value={template.additional_info} onChange={(e) => onUpdate("additional_info", e.target.value)} rows={8} />
        </div>
        <Button onClick={onSave} disabled={saving} className="w-full">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Template
        </Button>
      </CardContent>
    </Card>
  );
}

function PreviewRow({ label, value }: { label: string; value: string }) {
  return (
    <tr>
      <td style={{ padding: "8px 12px", fontWeight: 600, color: "#374151", fontSize: 13, width: "40%", borderBottom: "1px solid #f3f4f6" }}>{label}</td>
      <td style={{ padding: "8px 12px", color: "#1f2937", fontSize: 13, borderBottom: "1px solid #f3f4f6" }}>{value}</td>
    </tr>
  );
}

function PreviewHeader({ pc, template, subtitle }: { pc: string; template: EmailTemplate; subtitle: string }) {
  return (
    <>
      <div style={{ background: pc, padding: "24px 20px", textAlign: "center" }}>
        {template.logo_url ? (
          <img src={template.logo_url} alt="Logo" style={{ maxHeight: 50, margin: "0 auto 8px" }} />
        ) : (
          <div style={{ fontSize: 22, fontWeight: 700, color: "#fff", marginBottom: 4 }}>{template.company_name}</div>
        )}
        <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 13 }}>{subtitle}</div>
      </div>
    </>
  );
}

function PreviewFooter({ template }: { template: EmailTemplate }) {
  return (
    <div style={{ background: "#1f2937", padding: "16px 20px", textAlign: "center" }}>
      <p style={{ color: "#9ca3af", fontSize: 12, margin: 0 }}>© {new Date().getFullYear()} {template.company_name}. {template.footer_text}</p>
      <p style={{ color: "#9ca3af", fontSize: 12, margin: "4px 0 0" }}>{template.contact_email}</p>
    </div>
  );
}

function SectionBlock({ pc, title, children }: { pc: string; title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ background: pc, color: "#fff", padding: "8px 12px", fontSize: 13, fontWeight: 600, borderRadius: "6px 6px 0 0" }}>{title}</div>
      <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderTop: 0 }}>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function TransferTemplatePreview({ template }: { template: EmailTemplate }) {
  const pc = template.primary_color || "#2563eb";
  const d = TRANSFER_DEMO;
  return (
    <div style={{ background: "#f3f4f6", padding: 24, fontFamily: "Arial, sans-serif" }}>
      <div style={{ maxWidth: 600, margin: "0 auto", background: "#fff", borderRadius: 8, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
        <PreviewHeader pc={pc} template={template} subtitle="Booking Confirmation" />
        <div style={{ background: "#f0fdf4", padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e5e7eb" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#166534" }}>Total</span>
          <span style={{ fontSize: 20, fontWeight: 700, color: "#166534" }}>{d.total}</span>
        </div>
        <div style={{ padding: "24px 20px" }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: "#1f2937", marginBottom: 16 }}>{d.customer_name}, {template.greeting_text}</p>
          <SectionBlock pc={pc} title="Payment Summary">
            <PreviewRow label="Total" value={d.total} />
            <PreviewRow label="Booking NO" value={d.booking_code} />
            <PreviewRow label="Payment Method" value={d.payment_method} />
            <PreviewRow label="Full Name" value={d.customer_name} />
            <PreviewRow label="Phone" value={d.customer_phone} />
            <PreviewRow label="Email" value={d.customer_email} />
          </SectionBlock>
          <SectionBlock pc={pc} title="Route Details">
            <PreviewRow label="Pick-up Location" value={d.pickup_location} />
            <PreviewRow label="Drop Off" value={d.dropoff_location} />
            <PreviewRow label="Service" value={d.service} />
            <PreviewRow label="Trip Type" value={d.route_type} />
            <PreviewRow label="Passengers" value={String(d.passengers)} />
          </SectionBlock>
          <SectionBlock pc={pc} title="Flight & Schedule">
            <PreviewRow label="Pick-up Date" value={d.pickup_date} />
            <PreviewRow label="Arrival Time" value={d.arrival_time} />
            <PreviewRow label="Airline" value={d.airline} />
            <PreviewRow label="Flight Number" value={d.flight_number} />
            <PreviewRow label="Return Date" value={d.return_date} />
            <PreviewRow label="Return Airline" value={d.return_airline} />
            <PreviewRow label="Return Flight" value={d.return_flight} />
          </SectionBlock>
          {template.additional_info && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ background: pc, color: "#fff", padding: "8px 12px", fontSize: 13, fontWeight: 600, borderRadius: "6px 6px 0 0" }}>Important Information</div>
              <div style={{ border: "1px solid #e5e7eb", borderTop: 0, padding: "14px 12px", fontSize: 13, color: "#374151", whiteSpace: "pre-line", lineHeight: 1.6 }}>{template.additional_info}</div>
            </div>
          )}
        </div>
        <PreviewFooter template={template} />
      </div>
    </div>
  );
}

function TourTemplatePreview({ template }: { template: EmailTemplate }) {
  const pc = template.primary_color || "#2563eb";
  const d = TOUR_DEMO;
  return (
    <div style={{ background: "#f3f4f6", padding: 24, fontFamily: "Arial, sans-serif" }}>
      <div style={{ maxWidth: 600, margin: "0 auto", background: "#fff", borderRadius: 8, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
        <PreviewHeader pc={pc} template={template} subtitle="Tour Booking Confirmation" />
        <div style={{ background: "#f0fdf4", padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e5e7eb" }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#166534" }}>Total</span>
          <span style={{ fontSize: 20, fontWeight: 700, color: "#166534" }}>{d.total}</span>
        </div>
        <div style={{ padding: "24px 20px" }}>
          <p style={{ fontSize: 15, fontWeight: 600, color: "#1f2937", marginBottom: 16 }}>{d.customer_name}, {template.greeting_text}</p>
          <SectionBlock pc={pc} title="Payment Summary">
            <PreviewRow label="Total" value={d.total} />
            <PreviewRow label="Booking NO" value={d.booking_code} />
            <PreviewRow label="Payment Method" value={d.payment_method} />
            <PreviewRow label="Full Name" value={d.customer_name} />
            <PreviewRow label="Phone" value={d.customer_phone} />
            <PreviewRow label="Email" value={d.customer_email} />
          </SectionBlock>
          <SectionBlock pc={pc} title="Tour Details">
            <PreviewRow label="Tour" value={d.tour_name} />
            <PreviewRow label="Destination" value={d.destination} />
            <PreviewRow label="Date" value={d.excursion_date} />
            <PreviewRow label="Schedule" value={d.schedule} />
            <PreviewRow label="Duration" value={d.duration} />
            <PreviewRow label="Adults" value={String(d.adults)} />
            <PreviewRow label="Children" value={String(d.children)} />
            <PreviewRow label="Pickup Point" value={d.pickup_point} />
          </SectionBlock>
          {template.additional_info && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ background: pc, color: "#fff", padding: "8px 12px", fontSize: 13, fontWeight: 600, borderRadius: "6px 6px 0 0" }}>Important Information</div>
              <div style={{ border: "1px solid #e5e7eb", borderTop: 0, padding: "14px 12px", fontSize: 13, color: "#374151", whiteSpace: "pre-line", lineHeight: 1.6 }}>{template.additional_info}</div>
            </div>
          )}
        </div>
        <PreviewFooter template={template} />
      </div>
    </div>
  );
}
